import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Calendar, Settings, AlertCircle } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

export default function AccountSettings() {
  const { user } = useAuth();
  const { subscription, loading, openCustomerPortal } = useSubscription();
  const [isLoading, setIsLoading] = useState(false);

  const handleManageSubscription = async () => {
    setIsLoading(true);
    try {
      await openCustomerPortal();
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadgeVariant = (status: string | null | undefined) => {
    switch (status) {
      case 'active': return 'default';
      case 'trialing': return 'secondary';
      case 'past_due': return 'destructive';
      case 'canceled': return 'outline';
      default: return 'outline';
    }
  };

  const getStatusDisplay = (status: string | null | undefined) => {
    switch (status) {
      case 'active': return 'Active';
      case 'trialing': return 'Trial';
      case 'past_due': return 'Payment Required';
      case 'canceled': return 'Canceled';
      case 'unpaid': return 'Payment Failed';
      default: return 'New';
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container max-w-4xl mx-auto p-6">
        <div className="space-y-6">
          <div className="h-8 bg-muted rounded animate-pulse" />
          <div className="grid gap-6">
            <div className="h-48 bg-muted rounded animate-pulse" />
            <div className="h-32 bg-muted rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl mx-auto p-6">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Account Settings</h1>
          <p className="text-muted-foreground">Manage your subscription and billing</p>
        </div>

        {/* Subscription Status Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Subscription Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold">
                    {subscription?.subscription_tier || 'Free'}
                  </span>
                  <Badge variant={getStatusBadgeVariant(subscription?.subscription_status)}>
                    {getStatusDisplay(subscription?.subscription_status)}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {subscription?.subscription_status === 'trialing' && 'Trial period active'}
                  {subscription?.subscription_status === 'active' && 'Subscription active'}
                  {subscription?.subscription_status === 'past_due' && 'Payment method needs attention'}
                  {subscription?.subscription_status === 'canceled' && 'Subscription has been canceled'}
                  {!subscription?.subscription_status && 'No active subscription'}
                </p>
              </div>

              {/* Show appropriate button based on subscription state */}
              {((subscription?.subscription_status === 'past_due') || 
                (subscription?.subscription_status === 'expired') ||
                (subscription?.subscription_status === 'canceled' && subscription?.subscription_end_date && new Date(subscription.subscription_end_date) < new Date())) ? (
                <Button 
                  onClick={handleManageSubscription}
                  disabled={isLoading}
                  variant="destructive"
                  className="flex items-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  {isLoading ? 'Opening...' : 'Reactivate Plan'}
                </Button>
              ) : subscription?.subscribed && (
                <Button 
                  onClick={handleManageSubscription}
                  disabled={isLoading}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  {isLoading ? 'Opening...' : 'Manage Billing'}
                </Button>
              )}
            </div>

            <Separator />

            {/* Billing Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {subscription?.subscription_status === 'trialing' && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Trial Ends</p>
                  <p className="text-sm">{formatDate(subscription.trial_end_date)}</p>
                </div>
              )}
              
              {subscription?.subscription_status === 'active' && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Next Billing</p>
                  <p className="text-sm">{formatDate(subscription.subscription_end_date)}</p>
                </div>
              )}

              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Payment Method</p>
                <p className="text-sm">
                  {subscription?.payment_method_added ? 'Card on file' : 'No payment method'}
                </p>
              </div>
            </div>

            {/* Warning States */}
            {subscription?.subscription_status === 'past_due' && (
              <div className="flex items-start gap-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <AlertCircle className="w-5 h-5 text-destructive mt-0.5" />
                <div>
                  <p className="font-medium text-destructive">Payment Required</p>
                  <p className="text-sm text-muted-foreground">
                    Your payment method needs attention. Click "Manage Billing" to update.
                  </p>
                </div>
              </div>
            )}

            {subscription?.subscription_status === 'trialing' && !subscription?.payment_method_added && (
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Calendar className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Add Payment Method</p>
                  <p className="text-sm text-amber-700">
                    Add a payment method to get 500 bonus credits and extend your trial.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Email</p>
              <p className="text-sm">{user?.email}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">User ID</p>
              <p className="text-sm font-mono text-xs">{user?.id}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}